import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/img/logo.png";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DataContext } from "../context/DataContext";
import CryptoJS from "crypto-js";
import Loader from "../assets/img/loader.gif";
// import "./LoginForm.css";
import "../../src/assets/css/common1.css";

const LoginForm = () => {
  const [loader, setLoader] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const { setToken } = useContext(DataContext);
  const navigate = useNavigate();

  const apiUrl = process.env.REACT_APP_URL;
  const secretKey = "fship-password-10";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoader(true);

    let clientIp = "";

    try {
      const ipRes = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipRes.json();
      clientIp = ipData.ip || "";
    } catch (err) {
      console.log("IP fetch failed:", err);
    }

    // Login continues even if location permission is denied.
    if (!navigator.geolocation) {
      await loginUser(null, null, clientIp);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await loginUser(
          position.coords.latitude,
          position.coords.longitude,
          clientIp,
        );
      },
      async () => {
        await loginUser(null, null, clientIp);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  };

  const loginUser = async (latitude, longitude, clientIp) => {
    try {
      const encryptedPassword = CryptoJS.AES.encrypt(
        password,
        secretKey,
      ).toString();

      const response = await fetch(`${apiUrl}/employee/employeeLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password: encryptedPassword,
          latitude,
          longitude,
          clientIp,
        }),
      });

      const responseData = await response.json();

      if (responseData.status !== "success") {
        throw new Error(responseData.message || "Invalid login credentials");
      }
      const { Token, userType, department, accountType } = responseData.data;

      localStorage.setItem("_ux98c_v1", Token);
      localStorage.setItem("accountType", accountType);

      setToken(Token);

      toast.success("Login successful!");

      const dept = department?.trim().toLowerCase();

      if (userType === "1" || userType === 1) {
        navigate("/lead/dashboard");
      } else if (dept === "kam" || dept === "support") {
        navigate("/cases/dashboard");
      } else {
        navigate("/lead/dashboard");
      }
    } catch (err) {
      setError(err.message || "Unable to login. Please try again.");
      toast.error(err.message || "Login failed");
      setLoader(false);
    }
  };

  return (
    <div className="fship-login-page">
      <ToastContainer position="top-right" autoClose={2500} theme="colored" />

      {loader && (
        <div className="fship-loading-overlay">
          <img src={Loader} alt="Loading..." />
          <span>Signing you in...</span>
        </div>
      )}

      <main className="fship-login-container">
        {/* LEFT BRAND PANEL */}
        <section className="fship-brand-panel">
          <div className="fship-brand-header">
            <div className="fship-logo-surface">
              <img src={logo} alt="Fship Logo" />
            </div>

            <div className="fship-brand-divider"></div>

            <div className="fship-brand-caption">
              <span>BUSINESS</span>
              <strong>CRM PLATFORM</strong>
            </div>
          </div>

          <div className="fship-brand-content">
            <div className="fship-brand-badge">
              <span className="fship-live-dot"></span>
              ONE PLATFORM. COMPLETE CONTROL.
            </div>

            <h1>
              Everything your
              <br />
              business needs,
              <br />
              <span>in one place.</span>
            </h1>

            <p className="fship-brand-description">
              Manage leads, track customers, monitor your team and streamline
              your business operations with one powerful CRM platform.
            </p>

            <div className="fship-brand-features">
              <div className="fship-feature">
                <div className="fship-feature-icon">
                  <svg viewBox="0 0 24 24">
                    <circle cx="9" cy="8" r="4" />
                    <path d="M2 21v-2a7 7 0 0 1 14 0v2" />
                    <path d="M16 4.5a4 4 0 0 1 0 7.5" />
                    <path d="M19 14a6 6 0 0 1 3 5v2" />
                  </svg>
                </div>
                <span>Lead Management</span>
              </div>

              <div className="fship-feature">
                <div className="fship-feature-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M3 3v18h18" />
                    <path d="m7 14 4-4 4 3 6-7" />
                    <path d="M16 6h5v5" />
                  </svg>
                </div>
                <span>Sales Tracking</span>
              </div>

              <div className="fship-feature">
                <div className="fship-feature-icon">
                  <svg viewBox="0 0 24 24">
                    <circle cx="9" cy="8" r="3.5" />
                    <circle cx="18" cy="9" r="2.5" />
                    <path d="M2 21v-2a7 7 0 0 1 14 0v2" />
                    <path d="M17 16a5 5 0 0 1 5 5" />
                  </svg>
                </div>
                <span>Team Management</span>
              </div>

              <div className="fship-feature">
                <div className="fship-feature-icon">
                  <svg viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="3" />
                    <path d="m19.4 15 .1.1 1.4 1.1-1.4 2.5-1.7-.7a8 8 0 0 1-1.8 1l-.3 1.8h-3l-.3-1.8a8 8 0 0 1-1.8-1l-1.7.7-1.4-2.5 1.4-1.1a8 8 0 0 1 0-2l-1.4-1.1 1.4-2.5 1.7.7a8 8 0 0 1 1.8-1l.3-1.8h3l.3 1.8a8 8 0 0 1 1.8 1l1.7-.7 1.4 2.5-1.4 1.1a8 8 0 0 1-.1 2Z" />
                  </svg>
                </div>
                <span>Smart Operations</span>
              </div>
            </div>

            {/* Dashboard Preview */}
            {/* <div className="fship-dashboard-preview">
              <div className="fship-dashboard-top">
                <div>
                  <span className="fship-dashboard-mini-label">
                    YOUR WORKSPACE
                  </span>
                  <h3>Business Dashboard</h3>
                </div>
                <span className="fship-dashboard-dots">•••</span>
              </div>

              <div className="fship-dashboard-stats">
                <div>
                  <span>Total Leads</span>
                  <strong>1,248</strong>
                  <small>+12% this month</small>
                </div>

                <div>
                  <span>Customers</span>
                  <strong>892</strong>
                  <small>Growing steadily</small>
                </div>
              </div>

              <div className="fship-dashboard-chart">
                <div className="fship-chart-title">
                  <span>Business Overview</span>
                  <span>Monthly</span>
                </div>

                <div className="fship-chart-bars">
                  {[35, 55, 43, 72, 51, 83, 65, 95, 70, 88, 60, 100].map(
                    (height, index) => (
                      <div className="fship-chart-column" key={index}>
                        <span style={{ height: `${height}%` }}></span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div> */}
          </div>

          <div className="fship-brand-footer">
            <span>© 2024 Fship CRM</span>
            <span>Built for smarter business management</span>
          </div>

          <div className="fship-bg-circle fship-circle-one"></div>
          <div className="fship-bg-circle fship-circle-two"></div>
        </section>

        {/* RIGHT LOGIN PANEL */}
        <section className="fship-login-panel">
          <div className="fship-login-card">
            {/* Mobile Logo */}
            <div className="fship-mobile-logo">
              <div className="fship-mobile-logo-surface">
                <img src={logo} alt="Fship Logo" />
              </div>
              <span>BUSINESS CRM</span>
            </div>

            <div className="fship-login-heading">
              <span className="fship-heading-tag">ACCOUNT ACCESS</span>
              <h2>Welcome back!</h2>
              <p>Sign in to continue to your Fship CRM account.</p>
            </div>

            <form onSubmit={handleLogin}>
              {/* EMAIL */}
              <div className="fship-field">
                <label htmlFor="fship-email">Email Address</label>

                <div className="fship-input-box">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>

                  <input
                    id="fship-email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div className="fship-field">
                <label htmlFor="fship-password">Password</label>

                <div className="fship-input-box">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="11" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>

                  <input
                    id="fship-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    autoComplete="current-password"
                    required
                  />

                  <button
                    type="button"
                    className="fship-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                        <path d="M9.9 5.2A10.9 10.9 0 0 1 12 5c5 0 8.5 4.5 9 7-.2 1.1-1.1 2.5-2.4 3.7M6.2 6.2C3.9 7.7 2.3 10 2 12c.5 2.5 4 7 10 7 1.2 0 2.4-.3 3.4-.7" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* ERROR */}
              {error && (
                <div className="fship-login-error">
                  <span>!</span>
                  {error}
                </div>
              )}

              {/* OPTIONS */}
              <div className="fship-login-options">
                <span>
                  <span className="fship-security-indicator"></span>
                  Secure connection
                </span>

                <button
                  type="button"
                  onClick={() => navigate("/reset-password")}
                >
                  Forgot password?
                </button>
              </div>

              {/* SUBMIT */}
              <button
                type="submit"
                className="fship-submit-button"
                disabled={loader}
              >
                <span>{loader ? "Signing in..." : "Sign in to CRM"}</span>

                {!loader && (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                )}
              </button>
            </form>

            <div className="fship-login-divider">
              <span></span>
              <small>FSHIP CRM PLATFORM</small>
              <span></span>
            </div>

            <div className="fship-login-bottom">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Your workspace. Your business. One platform.
            </div>
          </div>

          <div className="fship-panel-footer">
            © {new Date().getFullYear()} Fship. All rights reserved.
          </div>
        </section>
      </main>
    </div>
  );
};

export default LoginForm;
